import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authProvider } from '@/services/currentAuthProvider'
import { redirectToApp } from '@/utils/app-redirect'

/**
 * Portal authentication guard
 *
 * Validates that:
 * 1. User is authenticated for protected routes
 * 2. User is of type 'client' (not behandelaar)
 *
 * If a behandelaar tries to access the portal, they are redirected to app.scrivio.nl
 */
export async function portalAuthGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  // If route doesn't require auth, allow access
  if (!requiresAuth) {
    // If authenticated and going to login, redirect to dashboard
    if (to.name === 'portal-login' && authStore.isAuthenticated) {
      return next({ name: 'portal-dashboard' })
    }
    return next()
  }

  // Route requires auth - check if user is authenticated
  if (!authStore.isAuthenticated) {
    return next({ name: 'portal-login', query: { redirect: to.fullPath } })
  }

  // User is authenticated - verify they are a client user
  if (authProvider.currentSession()) {
    try {
      const claims = await authProvider.getTokenClaims()
      const userType = claims?.userType as string | undefined

      if (userType !== 'client') {
        // Behandelaar trying to access portal - redirect to app
        // Must call next(false) to cancel navigation BEFORE redirect starts
        // Otherwise the component mounts and makes API calls that return 403
        // (Fixes SCRIVIO-FE-3S, SCRIVIO-FE-3V)
        next(false)
        redirectToApp()
        return
      }
    } catch {
      // If we can't verify the token, redirect to login
      return next({ name: 'portal-login' })
    }
  }

  // User is authenticated and is a client - allow access
  next()
}
